import { LucideIcon, Key, Globe, FileCode, ShieldCheck, Plug, KeyRound, FileSearch, Clock, Shield, ShieldAlert, Network, KeySquare, Wifi } from 'lucide-react';

// Tool component props - for URL-based tools that accept initial values
export interface ToolComponentProps {
  initialValue?: string;
  initialPort?: string;
}

// Tool definition interface
export interface ToolDefinition {
  id: string;
  name: string;
  description: string;
  path: string;
  icon: LucideIcon;
  tags: string[];
  isClientSide: boolean;
}

// Import tool components
import { JWTDecoder } from './JWTDecoder';
import { DNSLookup } from './DNSLookup';
import { Base64Tool } from './Base64Tool';
import { SSLChecker } from './SSLChecker';
import { PortChecker } from './PortChecker';
import { CertMatcher } from './CertMatcher';
import { CertDecoder } from './CertDecoder';
import { EpochConverter } from './EpochConverter';
import { TLSScanner } from './TLSScanner';
import { SecurityHeaders } from './SecurityHeaders';
import { CIDRCalculator } from './CIDRCalculator';
import { MTLSChecker } from './MTLSChecker';
import { MyIPChecker } from './MyIPChecker';

// Tool registry - add new tools here
export const tools: ToolDefinition[] = [
  {
    id: 'ssl-checker',
    name: 'SSL Checker',
    description: 'Check SSL/TLS certificate chain and validity',
    path: '/tools/ssl-checker',
    icon: ShieldCheck,
    tags: ['ssl', 'tls', 'certificate', 'chain', 'https'],
    isClientSide: false,
  },
  {
    id: 'port-checker',
    name: 'Port Checker',
    description: 'Check if ports are open on a server',
    path: '/tools/port-checker',
    icon: Plug,
    tags: ['port', 'scanner', 'network', 'tcp', 'firewall'],
    isClientSide: false,
  },
  {
    id: 'cert-matcher',
    name: 'Certificate & Key Matcher',
    description: 'Match certificate with private key and decode details',
    path: '/tools/cert-matcher',
    icon: KeyRound,
    tags: ['certificate', 'private key', 'pem', 'x509', 'ssl', 'match'],
    isClientSide: true,
  },
  {
    id: 'cert-decoder',
    name: 'Certificate & CSR Decoder',
    description: 'Decode X.509, CSR, PKCS#7, PKCS#12 certificates. Check EKU (Extended Key Usage), validity, and chain',
    path: '/tools/cert-decoder',
    icon: FileSearch,
    tags: ['certificate', 'csr', 'pkcs7', 'pkcs12', 'p7b', 'pfx', 'pem', 'x509', 'decode', 'eku', 'extended key usage', 'serverAuth', 'clientAuth'],
    isClientSide: true,
  },
  {
    id: 'tls-scanner',
    name: 'TLS version & Cipher Scanner',
    description: 'Check TLS versions and cipher suites supported by a server',
    path: '/tools/tls-scanner',
    icon: Shield,
    tags: ['tls', 'ssl', 'cipher', 'security', 'scan', 'protocol'],
    isClientSide: false,
  },
  {
    id: 'security-headers',
    name: 'Security Headers Checker',
    description: 'Analyze HTTP security headers like CSP, HSTS, X-Frame-Options',
    path: '/tools/security-headers',
    icon: ShieldAlert,
    tags: ['security', 'headers', 'csp', 'hsts', 'xfo', 'http', 'scan'],
    isClientSide: false,
  },
  {
    id: 'mtls-checker',
    name: 'mTLS CA Checker',
    description: 'Check acceptable root CA names for mutual TLS client certificates',
    path: '/tools/mtls-checker',
    icon: KeySquare,
    tags: ['mtls', 'tls', 'certificate', 'ca', 'client', 'mutual', 'authentication'],
    isClientSide: false,
  },
  {
    id: 'dns-lookup',
    name: 'DNS Lookup',
    description: 'Query DNS records for any domain',
    path: '/tools/dns-lookup',
    icon: Globe,
    tags: ['dns', 'domain', 'network', 'lookup'],
    isClientSide: false,
  },
  {
    id: 'my-ip',
    name: 'What is my IP?',
    description: 'Check your public IP address with CLI commands',
    path: '/tools/my-ip',
    icon: Wifi,
    tags: ['ip', 'address', 'public', 'network', 'cli', 'command'],
    isClientSide: false,
  },
  {
    id: 'base64',
    name: 'Base64 Encoder/Decoder',
    description: 'Encode and decode Base64 strings',
    path: '/tools/base64',
    icon: FileCode,
    tags: ['base64', 'encode', 'decode', 'conversion'],
    isClientSide: true,
  },
  {
    id: 'epoch-converter',
    name: 'Epoch Converter',
    description: 'Convert Unix timestamps to dates and vice versa',
    path: '/tools/epoch-converter',
    icon: Clock,
    tags: ['epoch', 'timestamp', 'unix', 'date', 'time', 'converter'],
    isClientSide: true,
  },
  {
    id: 'jwt-decoder',
    name: 'JWT Decoder',
    description: 'Decode and inspect JSON Web Tokens',
    path: '/tools/jwt-decoder',
    icon: Key,
    tags: ['jwt', 'token', 'decode', 'security'],
    isClientSide: true,
  },
  {
    id: 'cidr-calculator',
    name: 'CIDR & Subnet Visualizer',
    description: 'Calculate subnet details, IP ranges, and visualize CIDR notation',
    path: '/tools/cidr-calculator',
    icon: Network,
    tags: ['cidr', 'subnet', 'ip', 'network', 'calculator', 'mask'],
    isClientSide: true,
  },
];

// Export tool components
export const toolComponents: Record<string, React.ComponentType<ToolComponentProps>> = {
  'jwt-decoder': JWTDecoder,
  'dns-lookup': DNSLookup,
  'base64': Base64Tool,
  'ssl-checker': SSLChecker,
  'port-checker': PortChecker,
  'cert-matcher': CertMatcher,
  'cert-decoder': CertDecoder,
  'epoch-converter': EpochConverter,
  'tls-scanner': TLSScanner,
  'security-headers': SecurityHeaders,
  'cidr-calculator': CIDRCalculator,
  'mtls-checker': MTLSChecker,
  'my-ip': MyIPChecker,
};

// Helper to get tool by ID
export function getToolById(id: string): ToolDefinition | undefined {
  return tools.find((tool) => tool.id === id);
}

// Helper to get tool component by ID
export function getToolComponent(id: string): React.ComponentType | undefined {
  return toolComponents[id];
}
